package com.eladfinish.bible.contest.screens.search.history

import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.screens.search.model.SearchMode
import com.eladfinish.bible.contest.screens.search.model.SearchPlace

/**
 * The identity of a search in the history: two searches are the same entry only if all of these
 * are equal. Deliberately holds no result count and no chapter selection -- [searchPlace] only says
 * *whether* the search was limited to the mikud, never which chapters the mikud held at the time.
 */
data class SearchHistoryKey(
    val query: String,
    val onlyQri: Boolean = DEFAULT_ONLY_QRI,
    val withNikud: Boolean = DEFAULT_WITH_NIKUD,
    val withTeamim: Boolean = DEFAULT_WITH_TEAMIM,
    val searchMode: SearchMode = DEFAULT_SEARCH_MODE,
    val searchPlace: SearchPlace = DEFAULT_SEARCH_PLACE,
) {

    val kind: SearchHistoryKind
        get() = SearchHistoryKind.of(query)

    /**
     * Number, root and date searches ignore most of the search options, so those options must not
     * split one search into several entries: every option the search doesn't use is reset to a
     * fixed neutral value. Regular searches are returned unchanged.
     */
    fun normalized(): SearchHistoryKey =
        when (kind) {
            SearchHistoryKind.REGULAR -> this
            SearchHistoryKind.ROOT -> SearchHistoryKey(query = query, onlyQri = onlyQri)
            SearchHistoryKind.NUMBER, SearchHistoryKind.DATE -> SearchHistoryKey(query = query)
        }

    companion object {
        const val DEFAULT_ONLY_QRI = false
        const val DEFAULT_WITH_NIKUD = true
        const val DEFAULT_WITH_TEAMIM = true
        val DEFAULT_SEARCH_MODE = SearchMode.REGULAR_SEARCH
        val DEFAULT_SEARCH_PLACE = SearchPlace.WHOLE_BIBLE
    }
}

/**
 * Which engine a query runs on. Number, root and date searches are not [SearchMode]s: they are
 * picked from the query text alone (see PrepareLocalSearchRequestUseCase, whose checks this
 * mirrors in the same order), so the query is enough to tell them apart.
 */
enum class SearchHistoryKind {
    REGULAR,
    NUMBER,
    ROOT,
    DATE,
    ;

    companion object {
        fun of(query: String): SearchHistoryKind =
            when {
                query.startsWith(StringConstants.SHORESH) -> ROOT
                query.contains(StringConstants.DATE_SYMBOL) -> DATE
                query.startsWith(StringConstants.HASH_TAG_SYMBOL) -> NUMBER
                else -> REGULAR
            }
    }
}

data class SearchHistoryEntry(
    val key: SearchHistoryKey,
    val searchCount: Int,
    val firstSearchedAtMillis: Long,
    val lastSearchedAtMillis: Long,
)

enum class SearchHistorySort {
    /** Newest [SearchHistoryEntry.lastSearchedAtMillis] first. */
    RECENT,

    /** Highest [SearchHistoryEntry.searchCount] first; ties broken by the newest search. */
    MOST_SEARCHED,
}
